import { ClockIcon, PencilIcon } from '@heroicons/react/24/outline'
import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { usePermissions } from 'hooks/usePermissions'
import { docCategory } from 'pages/LoanSubmission/constants'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getShippingInfo, setShippingInfoHeader, updateShippingInfo } from 'services'
import { Button, Checkbox, Tooltip } from 'stories/components'
import { InputConvert, InputValidate, openAuditLog, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, inputGroups } from './constants'

/** Rendered as one grid row: left column stacks Warehouse + Seller; right column is Tracking. */
const WAREHOUSE_SELLER_TRACKING_GROUPS = new Set(['Warehouse Collateral', 'Seller Collateral', 'Tracking Info'])

export default function ShippingInformation() {
  const [changed, setChanged] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())
  const [data, setData] = useState<Record<string, any>>({})
  const [isLoading, setLoading] = useState(false)

  const loanNumber = setLoanNumber()
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()
  const canEditHeaders = hasPermission('Loan.Servicing.ShippingInformation.Header')

  const customHeaders = useSelector(
    (state: any) => (state.globalConfigReducer[GlobalConfigType.ShippingInfoHeaders] || {}) as Record<string, string>,
  )

  /** Resolve display title for a section, falling back to the default key when no custom title is set. */
  const headerOf = (key: string) => customHeaders[key] || key

  const onEditHeader = async (key: string) => {
    if (!canEditHeaders) return
    const current = headerOf(key)
    const next = (await prompt(`Edit section title (default: "${key}")`, { value: current })) as any
    if (next === false || next === undefined || next === null) return
    const trimmed = String(next).trim()
    try {
      const updated = (await setShippingInfoHeader(key, trimmed)) as Record<string, string>
      dispatch(setGlobalConfig(GlobalConfigType.ShippingInfoHeaders, updated || {}))
    } catch {}
  }

  const onShowHeaderHistory = (key: string) => {
    openAuditLog({
      table: 'ShippingInfoHeader',
      field: key,
      keys: { field: key },
      fieldLabel: `${key} (Section Title)`,
    })
  }

  const renderSectionTitle = (key: string) => (
    <div className="bg-gray-200 round-t py-1 px-4 flex items-center justify-between">
      <span>{headerOf(key)}</span>
      {canEditHeaders && (
        <div className="flex items-center gap-2">
          <Tooltip message="Edit section title">
            <span className="btn-icon cursor-pointer" onClick={() => onEditHeader(key)}>
              <PencilIcon className="w-4 h-4" />
            </span>
          </Tooltip>
          <Tooltip message="View edit history">
            <span className="btn-icon cursor-pointer text-gray-500" onClick={() => onShowHeaderHistory(key)}>
              <ClockIcon className="w-4 h-4" />
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  )

  useEffect(() => {
    const nInputs: any = cloneDeep(inputs)
    setLoading(true)

    getShippingInfo()
      .then((data) => {
        Object.keys(nInputs).forEach((key) => {
          nInputs[key].value = data[key]
        })
        nInputs['ServicingCode'].options = data['Investors']
        nInputs['InvestorCode'].options = data['Investors']
        setData(data)
        setInputs(nInputs)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const onChange = (key: string, value: string) => {
    if (key === 'documents') {
      const temp = cloneDeep(data)
      temp.documents = value
      setData(temp)
    } else {
      let newInputs = cloneDeep(inputs)
      value = InputConvert(newInputs[key], value)
      newInputs[key].value = value
      newInputs[key].error = InputValidate(newInputs[key])
      setInputs(newInputs)
    }
    setChanged(true)
  }

  const onSave = () => {
    let _data: Record<string, any> = {}

    Object.keys(inputs).forEach((key) => {
      _data[key] = inputs[key].value
    })

    _data.documents = data.documents

    setLoading(true)
    setChanged(false)
    updateShippingInfo(_data)
      .then(() => {
        toast('Successfully updated', { type: 'info' })
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'ShippingInfo',
      field: key,
      keys: {
        field: key,
      },
    }
    openAuditLog(options)
  }

  const renderInputGroupCard = (sectionTitle: string, fieldKeys: string[]) => (
    <div key={sectionTitle} className="shadow rounded border">
      {renderSectionTitle(sectionTitle)}
      <div className="p-2">
        <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
          {fieldKeys.map((item) => {
            const input = inputs[item]
            input.history = true
            return (
              <div className={`input md:col-span-${input.span || 1}`} key={item}>
                <RenderInput input={input} Key={item} onChange={onChange} showHistory={showHistory} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )

  return (
    <div className="shipping-information-container">
      <SaveChanges onSave={onSave} show={changed} />
      <div className="max-w-screen-2xl m-auto">
        <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
          <span>Shipping Information</span>
        </h2>
        <div className="bg-white">
          <div className="grid md:grid-cols-2 gap-5">
            <LayoutLoading show={isLoading} />

            {Object.keys(inputGroups)
              .filter((key) => !WAREHOUSE_SELLER_TRACKING_GROUPS.has(key))
              .map((key, index) => {
                const inputGroup = inputGroups[key]

                return (
                  <div key={index} className="md:col-span-1">
                    <div className="shadow rounded border">
                      {renderSectionTitle(key)}
                      <div className="p-2">
                        <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
                          {inputGroup.map((item: string) => {
                            const input = inputs[item]
                            input.history = true
                            return (
                              <div className={`input md:col-span-${input.span || 1}`} key={item}>
                                <RenderInput input={input} Key={item} onChange={onChange} showHistory={showHistory} />
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            <div className="md:col-span-1 flex flex-col gap-5">
              {renderInputGroupCard('Warehouse Collateral', inputGroups['Warehouse Collateral'])}
              {renderInputGroupCard('Seller Collateral', inputGroups['Seller Collateral'])}
            </div>
            <div className="md:col-span-1">{renderInputGroupCard('Tracking Info', inputGroups['Tracking Info'])}</div>
            <div className="col-span-full">
              <RenderInput
                input={{
                  inputType: 'FileTable',
                  title: 'Attachment Files',
                  required: false,
                  filePath: `loan/${loanNumber}/shippingInformation`,
                  showCategory: true,
                  showStatus: true,
                  multiple: true,
                  categories: docCategory().filter((v: string) => !!v),
                  additionalActions: [],
                  value: data.documents,
                  additionalRowActions: (document: any, index: number, onChange: Function) => {
                    return (
                      <Tooltip message="Internal/External">
                        <Checkbox
                          title=""
                          id={`side-document-intext-${document.id}`}
                          checked={document.intext}
                          onChange={(value: any) => onChange('intext', index, value)}
                        />
                      </Tooltip>
                    )
                  },
                }}
                Key="documents"
                key="documents"
                onChange={onChange}
              />
            </div>
            <div className="flex justify-center md:col-span-2">
              <Button className="w-full sm:w-[240px]" loading={isLoading} onClick={onSave}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
